import React, { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useAuth } from "../contexts/AuthContext";
import api from "../utils/axios";
import {
  BuildingOfficeIcon,
  UsersIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  UserGroupIcon,
  DocumentCheckIcon,
  ExclamationTriangleIcon,
  Squares2X2Icon,
  TicketIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const Dashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await api.get("/admin/dashboard");
        setDashboardData(response.data);
        setError(null);
      } catch (err) {
        setError("Failed to fetch dashboard data. Please try again later.");
        console.error("Dashboard data fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const formatTime = (ms) => {
    if (!ms) return "N/A";
    const hours = Math.floor(ms / (1000 * 60 * 60));
    return `${hours} hours`;
  };

  const formatPlanName = (planId) => {
    if (!planId) return "Unknown";
    const s = planId.replace(/_/g, " ");
    if (s.endsWith(" IN")) return s.slice(0, -3) + "(INR)";
    return s;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-200 border-t-primary mx-auto" />
          <p className="mt-4 text-gray-600 font-medium">Loading dashboard...</p>
          <p className="mt-1 text-sm text-gray-400">Fetching your metrics</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-xl">
        <div className="bg-red-50 border border-red-200/80 rounded-xl p-6 flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="font-semibold text-red-800">
              Unable to load dashboard
            </h3>
            <p className="mt-1 text-red-700 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return null;
  }

  const overviewCards = [
    {
      label: "Organizations",
      value: dashboardData.systemOverview.totalOrganizations ?? 0,
      icon: Squares2X2Icon,
      bg: "bg-slate-50",
      text: "text-slate-700",
    },
    {
      label: "Companies",
      value: dashboardData.systemOverview.totalCompanies,
      icon: BuildingOfficeIcon,
      bg: "bg-blue-50",
      text: "text-blue-700",
    },
    {
      label: "Users",
      value: dashboardData.systemOverview.totalUsers,
      icon: UsersIcon,
      bg: "bg-emerald-50",
      text: "text-emerald-700",
    },
    {
      label: "Documents",
      value: dashboardData.systemOverview.totalDocuments,
      icon: DocumentTextIcon,
      bg: "bg-amber-50",
      text: "text-amber-700",
    },
    {
      label: "Sign Requests",
      value: dashboardData.systemOverview.totalSignRequests,
      icon: PencilSquareIcon,
      bg: "bg-violet-50",
      text: "text-violet-700",
    },
    {
      label: "Blogs",
      value: dashboardData.systemOverview.totalBlogs ?? 0,
      icon: DocumentTextIcon,
      bg: "bg-rose-50",
      text: "text-rose-700",
    },
    {
      label: "Support Tickets (Open)",
      value: dashboardData.systemOverview.supportTicketsOpen ?? 0,
      icon: TicketIcon,
      bg: "bg-orange-50",
      text: "text-orange-700",
      sub: dashboardData.systemOverview.supportTicketsTotal != null &&
        `of ${dashboardData.systemOverview.supportTicketsTotal} total`,
    },
    {
      label: "AI Documents",
      value: dashboardData.systemOverview.aiDocumentsCount ?? 0,
      icon: SparklesIcon,
      bg: "bg-indigo-50",
      text: "text-indigo-700",
      sub: "Created by AI",
    },
  ];

  return (
    <div className="container w-full py-8">
      {/* Header */}
      <header className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
          {getGreeting()}
        </h1>
        <p className="mt-1 text-gray-500">
          Here’s what’s happening across your workspace.
        </p>
      </header>

      {/* System Overview Cards */}
      <section className="mb-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {overviewCards.map((card) => (
            <div
              key={card.label}
              className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-5 sm:p-6"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {card.label}
                  </p>
                  <p className="mt-2 text-2xl sm:text-3xl font-bold text-gray-900 tabular-nums">
                    {card.value}
                  </p>
                  {card.sub && (
                    <p className="mt-1 text-xs text-gray-400">{card.sub}</p>
                  )}
                </div>
                <div
                  className={`w-11 h-11 rounded-xl ${card.bg} flex items-center justify-center flex-shrink-0`}
                >
                  <card.icon className={`w-6 h-6 ${card.text}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Plan Statistics */}
      {dashboardData.planStatistics?.length > 0 && (
        <section className="mb-10">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                <CurrencyDollarIcon className="w-5 h-5 text-emerald-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                Organizations by Plan
              </h2>
            </div>
            <div className="p-5">
              <div className="h-64">
                <Bar
                  data={{
                    labels: dashboardData.planStatistics.map((p) =>
                      formatPlanName(p.plan ?? p._id),
                    ),
                    datasets: [
                      {
                        label: "US (USD)",
                        data: dashboardData.planStatistics.map((p) => {
                          if (p.us != null) return p.us;
                          if (p.in == null && p.count != null) return p.count;
                          return 0;
                        }),
                        backgroundColor: "rgba(16, 185, 129, 0.8)",
                      },
                      {
                        label: "IN (INR)",
                        data: dashboardData.planStatistics.map((p) =>
                          p.in ?? 0,
                        ),
                        backgroundColor: "rgba(245, 158, 11, 0.8)",
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "top",
                      },
                      tooltip: {
                        callbacks: {
                          afterBody: (items) => {
                            const idx = items[0]?.dataIndex;
                            if (idx == null) return "";
                            const p = dashboardData.planStatistics[idx];
                            const total =
                              ((p?.us ?? 0) + (p?.in ?? 0)) || (p?.count ?? 0);
                            return `Total: ${total}`;
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        grid: { display: false },
                      },
                      y: {
                        beginAtZero: true,
                        ticks: { stepSize: 1 },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Monthly Trends */}
      {dashboardData.monthlyTrends?.length > 0 && (() => {
        const trends = dashboardData.monthlyTrends.slice(-12);
        const monthNames = [
          "Jan", "Feb", "Mar", "Apr", "May", "Jun",
          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
        ];
        return (
          <section className="mb-10">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center">
                  <ChartBarIcon className="w-5 h-5 text-teal-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Sign Requests (Last 12 Months)
                </h2>
              </div>
              <div className="p-5">
                <div className="h-64">
                  <Bar
                    data={{
                      labels: trends.map(
                        (m) => `${monthNames[m._id.month - 1]} ${m._id.year}`,
                      ),
                      datasets: [
                        {
                          label: "Other",
                          data: trends.map((m) => {
                            const sent = m.sent ?? m.totalRequests ?? 0;
                            const done = m.done ?? m.completedRequests ?? 0;
                            const expired = m.expired ?? 0;
                            return Math.max(0, sent - done - expired);
                          }),
                          backgroundColor: "rgba(156, 163, 175, 0.8)",
                          stack: "stack0",
                        },
                        {
                          label: "Expired",
                          data: trends.map((m) => m.expired ?? 0),
                          backgroundColor: "rgba(245, 158, 11, 0.8)",
                          stack: "stack0",
                        },
                        {
                          label: "Done",
                          data: trends.map((m) => m.done ?? m.completedRequests ?? 0),
                          backgroundColor: "rgba(16, 185, 129, 0.8)",
                          stack: "stack0",
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: "top",
                        },
                        tooltip: {
                          callbacks: {
                            afterBody: (items) => {
                              const idx = items[0]?.dataIndex;
                              if (idx == null) return "";
                              const m = trends[idx];
                              const sent = m.sent ?? m.totalRequests ?? 0;
                              return `Sent: ${sent}`;
                            },
                          },
                        },
                      },
                      scales: {
                        x: {
                          stacked: true,
                          grid: { display: false },
                        },
                        y: {
                          stacked: true,
                          beginAtZero: true,
                          ticks: { stepSize: 1 },
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Signature Metrics */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden lg:col-span-2 mb-10">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center">
            <DocumentCheckIcon className="w-5 h-5 text-violet-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            Signature Metrics
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-100">
          {dashboardData.signatureMetrics.map((metric, index) => (
            <div
              key={index}
              className="bg-white p-5 hover:bg-gray-50/80 transition-colors"
            >
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                {metric._id}
              </p>
              <p className="mt-1 text-xl font-bold text-gray-900 tabular-nums">
                {metric.count} requests
              </p>
              {metric.avgResponseTime != null && (
                <p className="mt-1 text-sm text-gray-500">
                  Avg response {formatTime(metric.avgResponseTime)}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Top Active Organizations & Best Performing Company */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
        {/* Top 5 Active Organizations */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden flex flex-col max-h-[320px]">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3 flex-shrink-0">
            <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center">
              <Squares2X2Icon className="w-5 h-5 text-violet-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              Top 5 Active Organizations
            </h2>
          </div>
          <div className="divide-y divide-gray-100 overflow-y-auto min-h-0">
            {(dashboardData.topActiveOrganizations ?? []).length > 0 ? (
              dashboardData.topActiveOrganizations.map((org, index) => (
                <div
                  key={org.organizationId ?? index}
                  className="px-5 py-4 hover:bg-gray-50/80 transition-colors"
                >
                  <div className="flex justify-between items-center gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">
                        {org.name}
                      </p>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {org.completedRequests ?? 0} completed
                      </p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <p className="font-semibold text-gray-900 tabular-nums">
                        {org.totalRequests} requests
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-5 py-8 text-center text-gray-500 text-sm">
                No organization activity yet
              </div>
            )}
          </div>
        </div>

        {/* Best Performing Company */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3 flex-shrink-0">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
              <BuildingOfficeIcon className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              Best Performing Company
            </h2>
          </div>
          <div className="p-5 flex-1">
            {dashboardData.bestPerformingCompany ? (
              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-gray-900 text-lg">
                    {dashboardData.bestPerformingCompany.companyName}
                  </p>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {dashboardData.bestPerformingCompany.orgName}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completion Rate
                    </p>
                    <p className="mt-1 text-xl font-bold text-emerald-600 tabular-nums">
                      {dashboardData.bestPerformingCompany.completionRate}%
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Completed
                    </p>
                    <p className="mt-1 text-xl font-bold text-gray-900 tabular-nums">
                      {dashboardData.bestPerformingCompany.completed} /{" "}
                      {dashboardData.bestPerformingCompany.total}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-gray-500 text-sm">
                No company with 3+ sign requests yet
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Statistics */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden flex flex-col max-h-[420px]">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3 flex-shrink-0">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
              <BuildingOfficeIcon className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              Company Statistics
            </h2>
          </div>
          <div className="divide-y divide-gray-100 overflow-y-auto min-h-0">
            {dashboardData.companyStatistics.map((company, index) => (
              <div
                key={index}
                className="px-5 py-4 hover:bg-gray-50/80 transition-colors"
              >
                <div className="flex justify-between items-center gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">
                      {company.name}
                    </p>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Created {new Date(company.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="font-semibold text-gray-900 tabular-nums">
                      {company.totalRequests} requests
                    </p>
                    <p className="text-sm text-gray-500">
                      {company.activeUsers} active users
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Users */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden flex flex-col max-h-[420px]">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3 flex-shrink-0">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
              <UserGroupIcon className="w-5 h-5 text-emerald-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Top Users</h2>
          </div>
          <div className="divide-y divide-gray-100 overflow-y-auto min-h-0">
            {dashboardData.topUsers.map((u, index) => (
              <div
                key={index}
                className="px-5 py-4 hover:bg-gray-50/80 transition-colors"
              >
                <div className="flex justify-between items-center gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">
                      {u.name}
                    </p>
                    <p className="text-sm text-gray-500 truncate mt-0.5">
                      {u.email}
                    </p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="font-semibold text-gray-900 tabular-nums">
                      {u.totalRequests} requests
                    </p>
                    <p className="text-sm text-gray-500">
                      Last active {new Date(u.lastActive).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
